import { Accordion, AccordionDetails, AccordionSummary, Avatar, Box, Button, Card, Divider, Grid, Rating, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material'
import React, { memo } from 'react'
import { THorse } from '../../shared/types/horse.type'
import { t } from 'i18next'
import DistanceSvg from '../../assets/distance.svg'
import DurationSvg from '../../assets/duration.svg'
import CitySvg from '../../assets/city.svg'
import ShoeSvg from '../../assets/shoe.svg'
import horseTraining from '../../assets/horseTraining.svg'
import { GridArrowDownwardIcon } from '@mui/x-data-grid'
import { Ttraining, TWeather } from '../../shared/types/training.type'
import { compact, get, toNumber, upperCase } from 'lodash'
import { useSelector } from 'react-redux'
import { RootState } from '../../store'
import { Link, useNavigate } from 'react-router-dom'
import { routes } from '../../pages/routes'
import { convertSpeed } from '../../shared/utils/conversion'
import { TUser } from '../../shared/types/user.type'
import { Icon } from '@iconify/react'
import { useDispatch } from 'react-redux'
import { setAddVetModal, setAddVetModalExam } from '../../store/reducers/modal.reducer'
import CommentSection from './CommentSection'
import { useAuth } from "../../hooks/useAuth";
type InformationSectionProps = {
    horse: THorse | null,
    rider?: string | null
    trainingType?: string | null
    horseShoe?: string | null,
    track_name_auto?: string,
    track_surface_auto?: string,
    training_type_auto?: string,
    intensity?: number
    duration?: number,
    weather?: TWeather | null,
    track?: { id?: number, name?: string, trackSurfaceKey?: string, trackSurfaceName?: string, trackCondition?: string },
    insight?: Record<string, string | number>,
    isExamHasEcg: boolean,
    examId?: number,
    device?: string,
    date?: string,
    comment?: string,
    onEdit?: () => void,
    restrictedMode?: boolean,
    printable?: boolean,
    pitchTemperature?: null | number,
    humidity?: null | string,
}

function InformationSection({ pitchTemperature, humidity, printable = false, comment, onEdit, date, device, examId, horse, rider, intensity, duration, horseShoe, trainingType, weather, track, insight, track_name_auto, track_surface_auto, training_type_auto, restrictedMode }: InformationSectionProps) {
    const temperature = useSelector<RootState>(state => state.user.user?.temperature_unit) as "fahrenheit" | "celsius" | undefined
    const metric = useSelector<RootState>(state => state.user.user?.unit_system) as "metric" | "imperial" | undefined
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm")); // Check if screen is "sm" or smaller

    const { isUserTypePolo } = useAuth()

    const getWheterImage = () => {
        try {
            let icon = get(weather, 'data[0].weather.icon', null)

            return icon ? require(`../../assets/${icon}.png`) : require('../../assets/na.png')
        } catch {
            return require('../../assets/na.png')

        }
    }

    const getSurfaceImage = () => {
        try {
            if (track?.trackSurfaceName) {
                return require(`../../assets/track-surfaces/${track?.trackSurfaceKey}.png`)
            }
            return 'https://cdn-icons-png.flaticon.com/512/12131/12131234.png'

        } catch (error) {
            return 'https://cdn-icons-png.flaticon.com/512/12131/12131234.png'

        }


    }

    const convertDistanceKm = () => {
        let distance = get(insight, 'distance')

        if (!distance) {
            return ''
        }

        return convertSpeed(metric, toNumber(distance))
    }

    const getTrackName = () => {
        return get(track, 'name', track_name_auto) ?? "-"
    }

    const getWeathercode = (): string => {
        try {
            let code = get(weather, 'data[0].weather.code', "")

            return code ? t(`weather:code:${code}`) : ' - '
        } catch {
            return ' - '

        }
    }

    const route = (id?: number) => {
        if (!id || restrictedMode) return "#"
        return `${routes.DASHBOARD_ROUTES.TRACK_VIEW}`.replace(':id', String(id))

    }
    const getWeatherTemp = () => {
        try {
            const temp = get(weather, 'data[0].temp', null);

            if (!temp && !temperature) {
                return '- °C';
            }

            if (temp && temperature) {
                return convertTemperature(toNumber(temp), temperature);
            }

            return '- °C';
        } catch (err) {
            return '- °C';
        }
    };


    const convertTemperature = (temp?: number | null, unit?: "celsius" | "fahrenheit"): string => {
        if (!temp) return ''


        const _temp = unit === "fahrenheit"
            ? (temp * 1.8) + 32
            : temp;

        return _temp.toLocaleString(undefined, { minimumFractionDigits: 1 }) +
            (unit === "fahrenheit" ? "° F" : "° C");
    };


    const renderPichInfo = () => {
        if (isUserTypePolo) {
            return compact([convertTemperature(pitchTemperature, temperature), humidity]).join(' - ') || "-"
        } else {
            return ""
        }
    }

    const renderTrainingTypeTitle = () => {

        if (trainingType || training_type_auto) {
            if (isUserTypePolo) {

                return 'activity'

            } else {
                return "editTrainingForm:trainingType:label"
            }
        } else {
            return isUserTypePolo ? "block:unkownActivityType" : "block:unkownType"
        }

    }


    return (
        <Card >
            <Box px={2} py={1.5} display={'flex'} flexDirection={{ md: 'row', xs: 'column' }} alignItems={{ md: 'center', xs: 'start' }} flexWrap={{ md: 'nowrap', xs: 'wrap' }} gap={2} height={'100%'}>
                <Box minWidth={200} display={'flex'} flexDirection={printable ? 'row' : 'column'} gap={2} minHeight={'100%'}>
                    <Typography sx={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap' }} fontWeight={500} variant='caption'>{t('qualification:rider')} &#8200;:&#8200; <Typography variant='caption'>{rider ?? '-'}</Typography></Typography>
                    {
                        printable && <Divider flexItem orientation='vertical' />
                    }

                    <Typography sx={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap' }} fontWeight={500} variant='caption'>{t('eap:table:headers:intensity')} &#8200;:&#8200; <Rating size='small' value={intensity} readOnly></Rating></Typography>
                    {
                        printable && <Divider flexItem orientation='vertical' />
                    }
                    {
                        horseShoe &&
                        <>
                            <Typography sx={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap' }} fontWeight={500} variant='caption'>{t('editTrainingForm:horseShoeing:label')} &#8200;:&#8200; <Typography variant='caption'>{horseShoe}</Typography></Typography>

                            {
                                printable && <Divider flexItem orientation='vertical' />
                            }
                        </>

                    }
                    <Typography sx={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap' }} fontWeight={500} variant='caption'>{t('training:device')} &#8200;:&#8200; <Typography variant='caption'>{device ?? '-'}</Typography></Typography>
                </Box>
                <Divider flexItem orientation={isSmallScreen ? "horizontal" : "vertical"} />
                <CommentSection onEdit={onEdit} comment={comment} />

            </Box>
            <Divider></Divider>
            <Grid container spacing={2} sx={{ py: 2 }} >
                <Grid item md={2} xs={printable ? 4 : 6}>
                    <Box flex={1} display={'flex'} flexDirection={'column'} justifyContent={'center'} alignItems={'center'} minWidth={{ xs: '100px' }}>
                        <Icon icon='mdi:go-kart-track' width={40} height={40}></Icon>
                        <Typography textAlign={'center'} variant='body2' >{t(isUserTypePolo ? 'eap:table:headers:trackPolo' : 'eap:table:headers:track')}</Typography>
                        <Link
                            title={getTrackName()}
                            style={{
                                color: 'inherit', fontSize: '0.875rem', textAlign: 'center', fontStyle: track?.name ? 'normal' : 'italic', display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}
                            to={route(track?.id)}>{getTrackName()}
                        </Link>
                    </Box>
                </Grid>
                <Grid item md={2} xs={printable ? 4 : 6}>
                    <Box flex={1} display={'flex'} flexDirection={'column'} justifyContent={'center'} alignItems={'center'} minWidth={{ xs: '100px' }}>
                        <img alt='' style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', marginBlock: 1 }} src={getSurfaceImage()} ></img>
                        <Typography textAlign={'center'} variant='body2' > {t('eap:table:headers:trackSurface')} </Typography>
                        <Typography textAlign={'center'} fontStyle={!track_surface_auto ? 'normal' : 'italic'} fontSize={12} fontWeight={500} > {track?.trackSurfaceName ?? '-'} </Typography>
                        <Typography textAlign={'center'} fontSize={12} fontWeight={500} color={'#808080'}>{trainingType && track?.trackCondition}</Typography>
                    </Box>
                </Grid>
                <Grid item md={2} xs={printable ? 4 : 6}>
                    {
                        isUserTypePolo ?
                            <Box flex={1} display={'flex'} flexDirection={'column'} justifyContent={'center'} alignItems={'center'} minWidth={{ xs: '100px' }}>
                                <Icon icon='material-symbols-light:humidity-percentage-outline' width={40} height={40}></Icon>
                                <Typography textAlign={'center'} variant='body2' >{t('pitchTempAndHumidity')}</Typography>
                                <Typography textAlign={'center'} fontSize={12} fontWeight={500} >{renderPichInfo()}</Typography>
                            </Box>
                            : <Box flex={1} display={'flex'} flexDirection={'column'} justifyContent={'center'} alignItems={'center'} minWidth={{ xs: '100px' }}>
                                <Icon icon='game-icons:path-distance' width={40} height={40}></Icon>
                                <Typography textAlign={'center'} variant='body2' >{t('distance')} - {t('blocks:duration')}</Typography>
                                <Typography textAlign={'center'} fontSize={12} fontWeight={500} >{compact([convertDistanceKm(), duration ? duration + ' min' : null]).join(' - ')}</Typography>
                            </Box>
                    }

                </Grid>

                <Grid item md={2}
                    sx={{
                        order: !isUserTypePolo ? -1 : 'initial', // Move to first if POLO
                    }}
                    xs={printable ? 4 : 6}>
                    <Box flex={1} display={'flex'} flexDirection={'column'} justifyContent={'center'} alignItems={'center'} minWidth={{ xs: '100px' }} >
                        <Icon icon='maki:racetrack-horse' width={40} height={40}></Icon>
                        <Typography textAlign={'center'} variant='body2' >{t(renderTrainingTypeTitle())}</Typography>
                        {
                            (trainingType || training_type_auto) ? <Typography textAlign={'center'} fontSize={12} fontStyle={trainingType ? 'normal' : 'italic'} fontWeight={500} >{trainingType ?? training_type_auto ?? ''}</Typography> :
                                <Button disabled={restrictedMode} onClick={onEdit} size='small' variant='text'>{t(isUserTypePolo ? 'block:selectActivity' : 'block:selectType')}</Button>

                        }

                    </Box>
                </Grid>



                <Grid item md={2} xs={printable ? 4 : 6}>
                    <Box flex={1} display={'flex'} flexDirection={'column'} justifyContent={'center'} alignItems={'center'} minWidth={{ xs: '100px' }}>
                        <Icon icon='et:map' width={40} height={40}></Icon>
                        <Typography textAlign={'center'} variant='body2' >{t('blocks:location')}</Typography>
                        <Typography textAlign={'center'} fontSize={12} fontWeight={500} >{get(weather, 'city_name', '-')}</Typography>
                    </Box>
                </Grid>
                <Grid item md={2} xs={printable ? 4 : 6}>
                    <Box flex={1} display={'flex'} flexDirection={'column'} justifyContent={'center'} alignItems={'center'} minWidth={{ xs: '100px' }}>
                        <img alt='' style={{ width: 150, height: 40, marginBottom: 10, objectFit: 'contain' }}
                            onError={e => e.currentTarget = require('../../assets/na.png')}
                            src={getWheterImage()}></img>
                        <Typography textAlign={'center'} variant='body2' >{getWeathercode()}</Typography>
                        <Typography textAlign={'center'} fontSize={12} fontWeight={500} >{getWeatherTemp()}</Typography>
                    </Box>
                </Grid>
            </Grid>

        </Card>
    )
}

export default memo(InformationSection)